import React, { useRef, useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

/**
 * A horizontally-scrollable row that:
 *  - Shows left/right fade indicators when more content is off-screen
 *  - Has clickable arrow buttons on each edge for mouse users
 *  - Converts vertical mousewheel scroll → horizontal scroll when hovered
 *  - Supports native touch swipe on mobile
 * Scrollbar is hidden for clean visual.
 */
export default function ScrollableChipRow({ children, sx = {} }) {
  const scrollRef = useRef(null);
  const [overflow, setOverflow] = useState({ left: false, right: false });

  const updateOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const left = el.scrollLeft > 4;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
    setOverflow((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  useEffect(() => {
    updateOverflow();
    const el = scrollRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', updateOverflow, { passive: true });
    const ro = new ResizeObserver(updateOverflow);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateOverflow);
      ro.disconnect();
    };
  }, [updateOverflow, children]);

  // Mousewheel → horizontal scroll when there's no horizontal delta
  // (mice send deltaY; trackpads and horizontal wheels send deltaX).
  const handleWheel = useCallback((e) => {
    const el = scrollRef.current;
    if (!el) return;
    // Only intercept when the row actually overflows horizontally
    if (el.scrollWidth <= el.clientWidth) return;
    // If the event already has horizontal intent, let the browser handle it
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
    e.preventDefault();
    el.scrollBy({ left: e.deltaY, behavior: 'auto' });
  }, []);

  const scrollByAmount = useCallback((dir) => {
    const el = scrollRef.current;
    if (!el) return;
    // Scroll ~75% of visible width per click
    const delta = Math.round(el.clientWidth * 0.75) * dir;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  }, []);

  return (
    <Box sx={{ position: 'relative', mb: 2, ...sx }}>
      <Stack
        ref={scrollRef}
        direction="row"
        spacing={1}
        onWheel={handleWheel}
        sx={{
          flexWrap: 'nowrap',
          overflowX: 'auto',
          overflowY: 'hidden',
          pb: 0.5,
          mx: { xs: -1, sm: 0 },
          px: { xs: 1, sm: 0 },
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '& > *': { flexShrink: 0 },
          scrollBehavior: 'smooth',
        }}
      >
        {children}
      </Stack>

      {/* Left fade + arrow */}
      <Box
        aria-hidden={!overflow.left}
        sx={{
          pointerEvents: overflow.left ? 'auto' : 'none',
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 8,
          width: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          background: 'linear-gradient(to right, rgba(245,247,250,1) 0%, rgba(245,247,250,0.9) 60%, rgba(245,247,250,0))',
          opacity: overflow.left ? 1 : 0,
          transition: 'opacity 150ms ease',
        }}
      >
        <IconButton
          size="small"
          onClick={() => scrollByAmount(-1)}
          aria-label="Scroll left"
          sx={{
            backgroundColor: 'background.paper',
            boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
            '&:hover': { backgroundColor: 'background.paper' },
          }}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Right fade + arrow */}
      <Box
        aria-hidden={!overflow.right}
        sx={{
          pointerEvents: overflow.right ? 'auto' : 'none',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 8,
          width: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          background: 'linear-gradient(to left, rgba(245,247,250,1) 0%, rgba(245,247,250,0.9) 60%, rgba(245,247,250,0))',
          opacity: overflow.right ? 1 : 0,
          transition: 'opacity 150ms ease',
        }}
      >
        <IconButton
          size="small"
          onClick={() => scrollByAmount(1)}
          aria-label="Scroll right"
          sx={{
            backgroundColor: 'background.paper',
            boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
            '&:hover': { backgroundColor: 'background.paper' },
          }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}
